"use client";

import { type ReactNode } from "react";
import { cn } from "./cn";

const SIDE = {
  top: "bottom-full left-1/2 -translate-x-1/2 mb-1.5",
  bottom: "top-full left-1/2 -translate-x-1/2 mt-1.5",
  left: "right-full top-1/2 -translate-y-1/2 mr-1.5",
  right: "left-full top-1/2 -translate-y-1/2 ml-1.5",
} as const;

export interface TooltipProps {
  label: ReactNode;
  children: ReactNode;
  side?: keyof typeof SIDE;
}

/**
 * Lightweight CSS hover/focus tooltip — no portal, no JS positioning.
 * Wraps a single trigger and reveals the label on hover or keyboard focus.
 */
export function Tooltip({ label, children, side = "top" }: TooltipProps) {
  return (
    <span className="relative inline-flex group">
      {children}
      <span
        role="tooltip"
        className={cn(
          "pointer-events-none absolute z-50 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium",
          "bg-foreground text-background shadow-md",
          "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity",
          SIDE[side],
        )}
      >
        {label}
      </span>
    </span>
  );
}
